import { useCallback, useEffect, useMemo, useState } from 'react'

import { useQueryPOPDFLabelSetFields } from '@webapp/modules/purchasing/features/get-po-pdf-label-set-fields/models'
import {
  type IPOPDFLabelEditorSection,
  type IPOPDFLabelFieldRow,
} from '@webapp/modules/purchasing/features/get-po-pdf-label-set-fields/views/POPDFLabelSectionEditor'
import {
  PDFLabelSections,
  type PDFLabelSectionEnum,
} from '@webapp/modules/vendor/entities/enums/PDFLabelSectionEnum' // eslint-disable-line import/no-internal-modules
import {
  type PDFLabelFieldMetadata,
  type PDFLabelSet,
} from '@webapp/modules/vendor/entities/PDFLabelSet' // eslint-disable-line import/no-internal-modules

const SECTION_ORDER: PDFLabelSectionEnum[] = [
  PDFLabelSections.PO_FIELDS,
  PDFLabelSections.PO_COSTS,
  PDFLabelSections.ITEM_FIELDS,
]

/**
 * Title-case each word's first letter without lowercasing the rest, so
 * API-cased defaults like "PO Number" survive (Angular TitleCasePO parity).
 */
const toTitleCase = (label: string): string =>
  label.replace(/\w\S*/g, (word) => word[0].toUpperCase() + word.slice(1))

export interface UsePOPDFLabelSetFieldsPresenterParams {
  labelSet: PDFLabelSet
  onOverrideCommit: (
    translationType: PDFLabelFieldMetadata['translationType'],
    fieldKey: string,
    value: string
  ) => void
}

export const usePOPDFLabelSetFieldsPresenter = ({
  labelSet,
  onOverrideCommit,
}: UsePOPDFLabelSetFieldsPresenterParams) => {
  const { fields, isLoading } = useQueryPOPDFLabelSetFields()

  const [expandedSections, setExpandedSections] =
    useState<PDFLabelSectionEnum[]>(SECTION_ORDER)
  const [draftValues, setDraftValues] = useState<Record<string, string>>({})

  // Switching the active set discards in-progress (uncommitted) edits
  useEffect(() => {
    setDraftValues({})
  }, [labelSet.key])

  const sections = useMemo<IPOPDFLabelEditorSection[]>(() => {
    const toRow = (field: PDFLabelFieldMetadata): IPOPDFLabelFieldRow => {
      const rowId = `${field.translationType}:${field.fieldKey}`
      // Falsy saved values mean "no override" — display the default (BHV-035)
      const savedValue =
        labelSet.value[field.translationType][field.fieldKey] || undefined
      const existingLabel = toTitleCase(field.defaultLabel)

      return {
        rowId,
        fieldKey: field.fieldKey,
        existingLabel,
        value: draftValues[rowId] ?? savedValue ?? existingLabel,
        maxLength: field.maxLength,
        isCustomFieldLabel: field.isCustomFieldLabel,
        translationType: field.translationType,
      }
    }

    return SECTION_ORDER.map((section) => ({
      section,
      fields: fields.filter((field) => field.section === section).map(toRow),
    }))
  }, [fields, labelSet, draftValues])

  const onToggleSection = useCallback((section: PDFLabelSectionEnum) => {
    setExpandedSections((current) =>
      current.includes(section)
        ? current.filter((expanded) => expanded !== section)
        : [...current, section]
    )
  }, [])

  const handleOverrideChange = useCallback(
    (row: IPOPDFLabelFieldRow, value: string) => {
      setDraftValues((current) => ({ ...current, [row.rowId]: value }))
    },
    []
  )

  const handleOverrideCommit = useCallback(
    (row: IPOPDFLabelFieldRow) => {
      const draft = draftValues[row.rowId]
      const savedValue =
        labelSet.value[row.translationType][row.fieldKey] || undefined
      const committedValue = savedValue ?? row.existingLabel

      if (draft === undefined || draft === committedValue) return

      // The PUT belongs to save-po-pdf-label-overrides (ADR-9 command split)
      onOverrideCommit(row.translationType, row.fieldKey, draft)
    },
    [draftValues, labelSet, onOverrideCommit]
  )

  return {
    data: {
      sections,
    },
    states: {
      expandedSections,
      isLoading,
    },
    methods: {
      onToggleSection,
      handleOverrideChange,
      handleOverrideCommit,
    },
  }
}
